import { useState, useEffect, useCallback } from "react";
import { Grid, Paper } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FloatMenuCrud } from "components/FloatMenuCrud";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { debounce } from "lodash";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { getLinhas, getDadosPaginacaoLinhas, deleteLinha } from "services/modules/linhas";
import { ILinha } from "shared/interfaces/linha";
import { ModalLinha } from "./modalCadastroLinha";

export const Linhas = () => {
  const { openNotification } = useNavigation();
  const [linhas, setLinhas] = useState<ILinha[]>([]);
  const [totalItemsPagina, setTotalItens] = useState<number>(0);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const [isOpenModal, setIsOpenModal] = useState<{ isOpen: boolean; data?: ILinha }>({
    isOpen: false,
  });
  const [modalDelete, setModalDelete] = useState<{ isOpen: boolean; id?: number }>({
    isOpen: false,
  });

  const [filterColumnsDebounced, setFilterColumnsDebounced] = useState<{
    column?: "descricao";
    value?: string;
  }>();

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    sortBy: "",
    direction: "asc",
    search: "",
  });

  const debouncedFilterColumn = useCallback(
    debounce((column: "descricao", value: string) => {
      setFilterColumnsDebounced({ column, value });
    }, TIME_DEBOUNCE),
    []
  );

  const columns: GridColDef[] = [
    {
      field: "descricao",
      headerName: "Linhas",
      flex: 1,
      renderHeader: () => (
        <FilterColumn
          label="Linhas"
          name="descricao"
          onChange={(event) => {
            debouncedFilterColumn("descricao", event.target.value);
          }}
        />
      ),
    },
    {
      field: "tipoSistemaLinha",
      headerName: "Tipo de Sistema/Linha",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => params.row.tipoSistemaLinha || "-",
    },
    {
      field: "options",
      sortable: false,
      headerName: "Ações",
      align: "center",
      headerAlign: "center",
      flex: 0.15,
      renderCell: (params: any) => (
        <FloatMenuCrud
          onEdit={() => setIsOpenModal({ isOpen: true, data: params.row })}
          showDuplicateOption={false}
          deleteButtonText="Excluir"
          onDelete={() => setModalDelete({ isOpen: true, id: params.row.idLinha })}
        />
      ),
    },
  ];

  const getLinhasData = async (params?: any) => {
    setIsLoadingData(true);
    try {
      const result = await getLinhas(params);
      const total = await getDadosPaginacaoLinhas(params);

      setLinhas(result);
      setTotalItens(total);
    } catch (error: any) {
      openNotification({ message: "Não foi possível carregar as Linhas", type: "error" });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleDeleteLinha = async (id?: number) => {
    if (!id) return;
    setIsLoadingDelete(true);
    try {
      const result = await deleteLinha(id);
      if (result) {
        openNotification({ message: "Linha excluída com sucesso!", type: "success" });
        await getLinhasData(pagination);
      }
    } catch (error: any) {
      openNotification({ message: error?.message || "Erro ao excluir linha", type: "error" });
    } finally {
      setIsLoadingDelete(false);
      setModalDelete({ isOpen: false });
    }
  };

  const handlePagination = (updates: Partial<typeof pagination>) => {
    setPagination((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    if (filterColumnsDebounced) {
      const paginacao = {
        page: 1,
        pageSize: 10,
        sortBy: "",
        direction: pagination?.direction || "asc",
        search: filterColumnsDebounced.value || "",
      };
      handlePagination(paginacao);
      getLinhasData(paginacao);
    } else {
      getLinhasData(pagination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterColumnsDebounced]);

  useEffect(() => {
    getLinhasData(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize]);

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="CADASTRO DE LINHAS"
        label="Linhas"
        buttonLabel="Cadastrar"
        buttonOnClick={() => setIsOpenModal({ isOpen: true })}
        hasBackButton={false}
      />
      <Grid container display="flex" alignItems="center">
        <StackCustom>
          <Grid container mb={4}>
            <Grid item xs={12} sm={8} md={12}>
              <TitleTable title="Listagem de Linhas do MOF" />
            </Grid>
          </Grid>
          <Paper>
            <StripedDataGrid
              rows={linhas}
              columns={columns}
              getRowId={(row: ILinha) => row.idLinha}
              page={pagination.page - 1}
              pageSize={pagination.pageSize}
              rowCount={totalItemsPagina}
              loading={isLoadingData || isLoadingDelete}
              onPageChange={(newPage) => handlePagination({ page: newPage + 1 })}
              onPageSizeChange={(newPageSize) => {
                handlePagination({ pageSize: newPageSize, page: 1 });
              }}
              checkboxSelection={false}
              rowHeight={70}
            />
          </Paper>
        </StackCustom>
      </Grid>

      <ModalLinha
        isOpen={isOpenModal.isOpen}
        onClose={() => setIsOpenModal({ isOpen: false })}
        refreshData={() => getLinhasData(pagination)}
        dataLinha={isOpenModal.data}
      />

      <ConfirmDeleteModal
        title="Tem certeza que deseja excluir a Linha?"
        description="Essa operação não pode ser desfeita."
        open={modalDelete.isOpen}
        onClose={() => setModalDelete({ isOpen: false })}
        onConfirm={() => handleDeleteLinha(modalDelete.id)}
      />
    </DashboardStackCustom>
  );
};
